"use client";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/motion/tabs";
import { cn } from "@/lib/utils";
import { useState, type ReactNode } from "react";

export type ShowcaseTab = {
  value: string;
  icon: ReactNode;
  title: string;
  text: string;
  content: ReactNode;
};

export function ShowcaseTabs({
  tabs,
  className,
  stageClassName,
}: {
  tabs: ShowcaseTab[];
  className?: string;
  stageClassName?: string;
}) {
  const [value, setValue] = useState(tabs[0]?.value ?? "");

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-50",
        className,
      )}
    >
      <Tabs value={value} onValueChange={setValue} variant="underline">
        <TabsList
          className={cn(
            "grid w-full gap-0 border-neutral-200 bg-white",
            tabs.length === 4 ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-3",
          )}
        >
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              indicatorClassName="bg-neutral-900"
              className="group flex h-full flex-col items-start gap-1.5 px-5 pt-5 pb-5 text-left lg:px-6"
            >
              <span className="inline-flex items-center gap-2 text-[15px] font-medium [&_svg]:size-4 [&_svg]:shrink-0">
                {tab.icon}
                {tab.title}
              </span>
              <span className="hidden text-sm leading-snug text-neutral-400 group-aria-selected:text-neutral-500 sm:block">
                {tab.text}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
        <div
          className={cn(
            "dot-grid relative h-[520px] overflow-hidden px-4 pt-8 [mask-image:linear-gradient(to_bottom,black_82%,transparent)] sm:h-[600px] sm:px-8 lg:px-12 lg:pt-12",
            stageClassName,
          )}
        >
          {tabs.map((tab) => (
            <TabsContent key={tab.value} value={tab.value} className="mt-0">
              {tab.content}
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
}
